import { useEffect, useContext } from 'react';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import {
  ConnectionState,
  setConnectionState,
  setPeerReconnecting,
} from '../store/slices/connectionSlice';
import { sleep } from '../utils/sleep';
import RoomContext from '../lib/RoomClientContext';

function useConnection() {
  const roomClient = useContext(RoomContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const connectionState = useSelector(
    (state) => state.connection.connectionState
  );
  const deviceSetup = useSelector((state) => state.device.deviceSetup);
  const roomId = useSelector((state) => state.room.roomId);

  const [query, setQuery] = useSearchParams();

  useEffect(() => {
    query.get('peerReconnecting') === 'true' &&
      dispatch(setPeerReconnecting(true));
    // remove peerReconnecting from the query
    query.delete('peerReconnecting');
    setQuery(query);
  }, [query, setQuery, dispatch]);

  const retryDelayMilliseconds = 5000;
  const retryTimeLimit = 45 * 1000;

  async function fetchToken() {
    const queryTokenId = query.get('tokenid');
    await roomClient.fetchToken({ queryTokenId, roomId, t, navigate });
  }

  async function configureToken() {
    try {
      await roomClient.configureToken(query, setQuery);
    } catch (error) {
      logger.log('Error configuring token: ', error);
    }
  }

  async function retryWithTimeoutAndDelay({
    action,
    timeLimit,
    successAction,
    failureAction,
    retryDelayMilliseconds,
  }) {
    const startTime = Date.now();

    while (true) {
      try {
        await action();
        // If the async function completes without error, execute the success action.
        successAction();
        break;
      } catch (error) {
        const currentTime = Date.now();
        const elapsedTime = currentTime - startTime;
        Sentry.captureException(
          new Error(`${error.message} (state: ${connectionState})`, {
            fingerprint: ['{{ default }}', error.message],
          })
        );

        if (elapsedTime >= timeLimit) {
          // If the time limit is exceeded, execute the failure action.
          failureAction();
          break;
        } else {
          // logger.error(error.message);
          Sentry.captureException(
            new Error(`${action.name} error: ${error.message}`)
          );
          // Sleep for the specified duration before the next retry.
          console.log(
            'ConnectionState is sleeping for ',
            retryDelayMilliseconds,
            connectionState
          );
          await sleep(retryDelayMilliseconds);
        }
      }
    }
  }

  useEffect(() => {
    // This useEffect handles the connection mechanism
    logger.log('Connection state changed to: ', connectionState);
    switch (connectionState) {
      case ConnectionState.UNINITIALIZED:
        // Load token from sessionStorage
        // You only get one shot
        (async () => {
          try {
            await fetchToken();
            dispatch(setConnectionState(ConnectionState.INITIALIZED));
          } catch (err) {
            logger.error(err.message.toUpperCase());
            Sentry.captureException(err);
          }
        })();
        break;
      case ConnectionState.INITIALIZED:
        // Configure the connection by getting the token
        // You only get one shot
        (async () => {
          try {
            await configureToken();
            dispatch(setConnectionState(ConnectionState.CONFIGURED));
          } catch (err) {
            logger.error(err.message.toUpperCase());
            throw err;
          }
        })();
        break;
      case ConnectionState.RECONNECTING:
        const tries = 10;
        let connected = false;

        (async () => {
          for (let i = 0; i < tries && !connected; i++) {
            try {
              if (roomClient.socket.connected) {
                await roomClient._checkIfIceRetartRequired();
                if (
                  roomClient.producerTransport.connectionState === 'connected'
                ) {
                  connected = true;
                  dispatch(setConnectionState(ConnectionState.CONNECTED));
                }
              }
            } catch (err) {
              logger.error('Room client fail');
              Sentry.captureException(err);
            }

            if (!connected) {
              await sleep(retryDelayMilliseconds);
            }
          }
        })();
        break;
      case ConnectionState.CONFIGURED:
        // Connect the socket with a retry mechanism
        retryWithTimeoutAndDelay({
          action: async () => await roomClient.initializeSocketConnection(),
          retryTimeLimit,
          successAction: () => {
            dispatch(setConnectionState(ConnectionState.CONNECTED));
          },
          failureAction: () => {
            dispatch(setConnectionState(ConnectionState.FAILED));
          },
          retryDelayMilliseconds,
        });
        break;
      case ConnectionState.CONNECTED:
        if (!deviceSetup) return;
        retryWithTimeoutAndDelay({
          action: async () => await roomClient.setupConnection(),
          retryTimeLimit,
          successAction: () => {
            dispatch(setPeerReconnecting(false));
          },
          failureAction: () => {
            dispatch(setConnectionState(ConnectionState.FAILED));
          },
          retryDelayMilliseconds,
        });
        break;

      case ConnectionState.FAILED:
        // stop trying to connect
        roomClient.close();
        break;
      default:
        logger.error('Unknown case connectionState', connectionState);
    }
  }, [connectionState, deviceSetup]);

  return null;
}

export default useConnection;
